import React, { useMemo } from "react";

/**
 * Event header for admin editing context.
 * Props:
 *  - eventDetails: event object (must include Name, ShortName, EventId, LeagueId, DateTime, Active, Season, Fights[])
 *  - status: string ("ready" | "in progress" | other) to color the pill (green/red)
 */
const EventAdminHeader = ({ eventDetails, status = "in progress" }) => {
  const {
    Name,
    ShortName,
    EventId,
    LeagueId,
    DateTime,
    Active,
    Season,
    Fights = [],
  } = eventDetails || {};

  const {
    dt,
    totalFights,
    activeFights,
    canceledFights,
    mainCardCount,
    prelimsCount,
    featuredTitle,
  } = useMemo(() => {
    const dt = DateTime ? new Date(DateTime) : null;
    const totalFights = Fights.length;
    const activeFights = Fights.filter(
      (f) => f?.Active !== false && f?.Status !== "Canceled"
    ).length;
    const canceledFights = Fights.filter(
      (f) => f?.Active === false || f?.Status === "Canceled"
    ).length;
    const mainCard = Fights.filter(
      (f) => f?.CardSegment === "Main Card" && f?.Status !== "Canceled"
    );
    const prelims = Fights.filter(
      (f) => f?.CardSegment === "Prelims" && f?.Status !== "Canceled"
    );

    // Featured fight: 5 rounds, else first Main Card by Order
    const featured =
      Fights.find((f) => f?.Rounds === 5 && f?.Status !== "Canceled") ||
      [...mainCard].sort(
        (a, b) =>
          (a?.Order ?? Number.MAX_SAFE_INTEGER) -
          (b?.Order ?? Number.MAX_SAFE_INTEGER)
      )[0];

    const featuredTitle = featured?.Fighters?.length
      ? featured.Fighters.map((x) => `${x.FirstName} ${x.LastName}`).join(
          " vs. "
        )
      : null;

    return {
      dt,
      totalFights,
      activeFights,
      canceledFights,
      mainCardCount: mainCard.length,
      prelimsCount: prelims.length,
      featuredTitle,
    };
  }, [DateTime, Fights]);

  return (
    <div className="card p-5 md:p-6">
      {/* Title + status pill */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-bold text-dark-grey leading-tight">
            {Name || "Event"}
          </h1>
          <p className="text-sm text-medium-grey mt-1">
            {ShortName ? `${ShortName} • ` : ""}
            Event ID:{" "}
            <span className="font-medium text-dark-grey">{EventId}</span> •
            League:{" "}
            <span className="font-medium text-dark-grey">{LeagueId}</span>
          </p>
        </div>

        {/* Status pill (green for ready, deep red for in progress) */}
        <div
          className="segmented"
          role="tablist"
          aria-label="Event status quick view"
        >
          <span
            role="tab"
            aria-selected
            className={`segmented__btn is-active ${
              status === "ready" ? "is-success" : "is-danger"
            }`}
          >
            {status === "ready" ? "Ready" : "In Progress"}
          </span>
        </div>
      </div>

      {/* Meta row */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Date / Time */}
        <div className="panel">
          <div className="text-xs font-semibold text-medium-grey mb-1">
            Date & Time
          </div>
          {dt ? (
            <>
              <div className="text-dark-grey font-bold">
                {dt.toLocaleDateString([], {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
              <div className="text-medium-grey text-sm">
                {dt.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </>
          ) : (
            <div className="text-medium-grey text-sm">—</div>
          )}
        </div>

        {/* Card breakdown */}
        <div className="panel">
          <div className="text-xs font-semibold text-medium-grey mb-1">
            Card Breakdown
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="chip chip--muted">Total: {totalFights}</span>
            <span className="chip chip--ok">Active: {activeFights}</span>
            <span className="chip chip--warn">Canceled: {canceledFights}</span>
            <span className="chip chip--info">Main: {mainCardCount}</span>
            <span className="chip chip--info">Prelims: {prelimsCount}</span>
          </div>
        </div>

        {/* Featured fight */}
        <div className="panel">
          <div className="text-xs font-semibold text-medium-grey mb-1">
            Featured Fight
          </div>
          <div className="text-sm font-bold text-dark-grey line-clamp-2">
            {featuredTitle || "—"}
          </div>
        </div>
      </div>

      {/* Active + Season */}
      <div className="mt-3 text-xs text-medium-grey">
        Event Active:{" "}
        <span
          className={`font-semibold ${
            Active ? "text-green-700" : "text-gray-600"
          }`}
        >
          {Active ? "Yes" : "No"}
        </span>{" "}
        • Season:{" "}
        <span className="font-semibold text-dark-grey">{Season ?? "—"}</span>
      </div>
    </div>
  );
};

export default EventAdminHeader;
